import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Alert, Button, Input, Modal, Spin, Table, Tooltip, message } from "antd";
import { pick, toNumber } from "@components/health/clients/health-clients-utils";
import { deleteSprKompl, fetchSprKomplList } from "./spr-kompl-api";
import SprKomplDrawer from "./spr-kompl-drawer";
import "./spr-kompl.css";

const normalizeRow = (row) => ({
  idKompl: toNumber(pick(row, "idKompl", "IdKompl")),
  name: pick(row, "name", "Name") ?? "",
  komment: pick(row, "komment", "Komment") ?? "",
  cntCount: toNumber(pick(row, "cntCount", "CntCount")) ?? 0
});

const SprKompl = () => {
  const [items, setItems] = useState([]);
  const [nameFilter, setNameFilter] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerId, setDrawerId] = useState(null);
  const [drawerIsNew, setDrawerIsNew] = useState(false);

  const listReqRef = useRef(0);

  const loadList = useCallback(() => {
    const requestId = ++listReqRef.current;
    setLoadingList(true);
    setError("");
    return fetchSprKomplList()
      .then((data) => {
        if (requestId !== listReqRef.current) return;
        const rows = Array.isArray(data)
          ? data.map(normalizeRow).filter((r) => r.idKompl != null)
          : [];
        setItems(rows);
        setSelectedId((prev) => {
          if (prev != null && rows.some((r) => r.idKompl === prev)) return prev;
          return null;
        });
      })
      .catch((e) => {
        if (requestId !== listReqRef.current) return;
        setError(e.message || "Не удалось загрузить комплекты");
        setItems([]);
      })
      .finally(() => {
        if (requestId === listReqRef.current) setLoadingList(false);
      });
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const filteredItems = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((t) => (t.name || "").toLowerCase().includes(q));
  }, [items, nameFilter]);

  const selectedItem = useMemo(
    () => items.find((t) => t.idKompl === selectedId) || null,
    [items, selectedId]
  );

  const openCreate = () => {
    setDrawerIsNew(true);
    setDrawerId(null);
    setDrawerOpen(true);
  };

  const openEdit = (row) => {
    if (row?.idKompl == null) return;
    setSelectedId(row.idKompl);
    setDrawerIsNew(false);
    setDrawerId(row.idKompl);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const onDrawerSaved = (saved) => {
    const id = toNumber(pick(saved, "idKompl", "IdKompl"));
    loadList().then(() => {
      if (id != null) {
        setSelectedId(id);
        setDrawerId(id);
        setDrawerIsNew(false);
      }
    });
  };

  const onDelete = () => {
    if (!selectedItem) return;
    Modal.confirm({
      title: "Удалить?",
      content: `Комплект «${selectedItem.name || selectedItem.idKompl}» будет удалён.`,
      okText: "Удалить",
      okType: "danger",
      cancelText: "Отмена",
      onOk: () =>
        deleteSprKompl(selectedItem.idKompl)
          .then(() => {
            message.success("Комплект удалён");
            if (drawerId === selectedItem.idKompl) closeDrawer();
            setSelectedId(null);
            return loadList();
          })
          .catch((e) => {
            message.error(e.message || "Не удалось удалить");
            return Promise.reject(e);
          })
    });
  };

  const columns = [
    {
      title: "Наименование",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      render: (name, row) => name || `#${row.idKompl}`
    },
    {
      title: "Комментарий",
      dataIndex: "komment",
      key: "komment",
      ellipsis: true
    },
    {
      title: "Позиций",
      dataIndex: "cntCount",
      key: "cntCount",
      width: 90,
      align: "right"
    }
  ];

  return (
    <div className="spr-kompl">
      {error ? (
        <Alert className="spr-kompl__error" type="error" showIcon message={error} />
      ) : null}

      <div className="spr-kompl__body">
        <div className="spr-kompl__list">
          <div className="spr-kompl__toolbar">
            <div className="spr-kompl__extra-actions">
              <Tooltip title="Добавить">
                <span>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    aria-label="Добавить"
                    onClick={openCreate}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Изменить">
                <span>
                  <Button
                    icon={<EditOutlined />}
                    disabled={!selectedItem}
                    aria-label="Изменить"
                    onClick={() => openEdit(selectedItem)}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Удалить">
                <span>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    disabled={!selectedItem}
                    aria-label="Удалить"
                    onClick={onDelete}
                  />
                </span>
              </Tooltip>
            </div>
            <Input
              allowClear
              variant="borderless"
              className="spr-kompl__filter"
              placeholder="Фильтр по наименованию"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
          </div>
          <div className="spr-kompl__list-body">
            {loadingList && !items.length ? (
              <div className="spr-kompl__center">
                <Spin />
              </div>
            ) : (
              <Table
                className="spr-kompl__table"
                size="small"
                rowKey="idKompl"
                pagination={false}
                columns={columns}
                dataSource={filteredItems}
                loading={loadingList}
                rowClassName={(row) =>
                  row.idKompl === selectedId ? "spr-kompl__row--selected" : ""
                }
                onRow={(row) => ({
                  onClick: () => {
                    setSelectedId(row.idKompl);
                    openEdit(row);
                  }
                })}
                locale={{
                  emptyText: items.length ? "Ничего не найдено" : "Нет комплектов"
                }}
              />
            )}
          </div>
        </div>
      </div>

      <SprKomplDrawer
        open={drawerOpen}
        idKompl={drawerId}
        isNew={drawerIsNew}
        onClose={closeDrawer}
        onSaved={onDrawerSaved}
      />
    </div>
  );
};

export default SprKompl;
